import { useEffect, useState } from 'react'
import { watchGames, deleteGame } from './ggGamesData.js'
import { getPlayable, getGameDef, PLAYABLE_GAMES } from './ggGameRegistry.js'
import { GAME_GUIDES, HANDICAP_GUIDE } from './ggGameGuides.js'
import GGameSetup from './GGameSetup.jsx'
import GScorecard from './GScorecard.jsx'
import GGameResults from './GGameResults.jsx'
import CLUB from '../../config/club.js'
import { HUSTLER } from '../../config/siteMode.js'
import { useNavigate } from 'react-router-dom'
import { loadMemberGame } from '../../lib/memberGgc.js'
import { pickGameFile } from './ggGameFile.js'

// G-Play — the real playable center (frame orchestrator). Home shows game tiles
// (tap to start) + saved games (resume/delete). New Game → setup → live
// scorecard → results. All state lives under ggGames/*, walled off from tourney.
const cell = { border: '1px solid #ddd', padding: '6px 10px', fontSize: 16 }
const GREEN = '#1B4D2E'

// App-matched "Choose your game" tile — mirrors GolfGamesScorecard's renderGame +
// card* styles (app/(tabs)/index.tsx): white card, icon on top, bold name, a
// one-line (2-max) description, a players pill, and the TEAMS badge when teams.
const tile = {
  card: { width: 240, backgroundColor: CLUB.creamColor, borderRadius: 18, padding: 14, boxShadow: '0 4px 5px rgba(0,0,0,0.18)', textAlign: 'left', cursor: 'pointer', border: 'none' },
  icon: { fontSize: 28, marginBottom: 8, display: 'block', lineHeight: 1 },
  name: { fontSize: 15, fontWeight: 800, color: '#0F2914', marginBottom: 4 },
  desc: { fontSize: 15, color: '#5C7562', lineHeight: '17px', marginBottom: 10, fontWeight: 500, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' },
  meta: { display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 6 },
  players: { fontSize: 11, fontWeight: 900, color: '#386641', backgroundColor: '#EDF4EE', padding: '3px 8px', borderRadius: 8 },
  tag: { fontSize: 10, fontWeight: 900, color: '#7A4F00', backgroundColor: '#FFFBE6', padding: '3px 8px', borderRadius: 8 },
}

function GameTile({ game, onGuide, selected }) {
  // Golf Hustler: dark tile to match the CREATE GAME look (lime title, gray desc,
  // yellow selected border). Styling only — same click/behavior.
  const cardStyle = HUSTLER
    ? { ...tile.card, backgroundColor: '#121212', boxShadow: 'none', border: selected ? '2px solid #F2C400' : '2px solid #2b2b2b' }
    : { ...tile.card, border: selected ? '2px solid #1B4D2E' : '2px solid transparent' }
  const nameStyle = HUSTLER ? { ...tile.name, color: '#9CC400' } : tile.name
  const descStyle = HUSTLER ? { ...tile.desc, color: '#9aa08f' } : tile.desc
  return (
    <button onClick={() => onGuide(game.key)} style={cardStyle} className="transition hover:brightness-[0.98]">
      <span style={tile.icon}>{game.icon}</span>
      <div style={nameStyle}>{game.label}</div>
      <div style={descStyle}>{game.description}</div>
    </button>
  )
}

// The how-to guide panel shown when a tile is clicked.
function GameGuide({ game, guide }) {
  if (!game) return null
  return (
    <div className="rounded-2xl p-5 mt-2" style={{ background: CLUB.creamColor, border: '1.5px solid #1B4D2E' }}>
      <div className="flex items-center gap-3 mb-3">
        <span style={{ fontSize: 30 }}>{game.icon}</span>
        <div>
          <h4 className="text-xl font-extrabold" style={{ color: '#0F2914' }}>{game.label}</h4>
          {guide?.players && <div className="text-sm font-semibold text-gray-500">{guide.players}</div>}
        </div>
      </div>
      {!guide ? <p className="text-base text-gray-700">{game.description}</p> : (
        <div className="space-y-4">
          <div>
            <div className="text-sm font-extrabold uppercase tracking-wide text-gray-500 mb-1">How to play</div>
            <ul className="list-disc pl-5 space-y-1.5 text-base text-gray-800">{guide.how.map((h, i) => <li key={i}>{h}</li>)}</ul>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="rounded-xl p-4" style={{ background: '#F1F5F9', border: '1px solid #CBD5E1' }}>
              <div className="text-sm font-extrabold uppercase tracking-wide text-gray-600 mb-1">😄 Without bets (points / fun)</div>
              <p className="text-base text-gray-800">{guide.noBet}</p>
            </div>
            <div className="rounded-xl p-4" style={{ background: '#FEF6E7', border: '1px solid #EBCB8B' }}>
              <div className="text-sm font-extrabold uppercase tracking-wide text-gray-600 mb-1">💰 With bets (money)</div>
              <p className="text-base text-gray-800">{guide.withBet}</p>
            </div>
          </div>
          <div className="rounded-xl p-4" style={{ background: '#EDF4EE', border: '1px solid #C7DFCB' }}>
            <div className="text-sm font-extrabold uppercase tracking-wide text-gray-600 mb-2">Example setup</div>
            <p className="text-base text-gray-800"><b>Setup:</b> {guide.example.setup}</p>
            <p className="text-base text-gray-800 mt-1"><b>Players:</b> {guide.example.players}</p>
            <p className="text-base text-gray-800 mt-1"><b>How it plays out:</b> {guide.example.walk}</p>
          </div>
        </div>
      )}
    </div>
  )
}

// One shared handicap explainer — same for every game.
function HandicapGuide() {
  return (
    <div className="rounded-2xl p-5" style={{ background: '#EAF2FB', border: '1.5px solid #4C7BB5' }}>
      <h4 className="text-xl font-extrabold mb-1" style={{ color: '#123D66' }}>🎯 Handicaps — how they make any game competitive for any handicap</h4>
      <p className="text-base text-gray-800 mb-3">{HANDICAP_GUIDE.purpose}</p>
      <div className="space-y-3">
        {HANDICAP_GUIDE.steps.map(([t, d]) => (
          <div key={t}>
            <div className="text-base font-extrabold text-gray-800">{t}</div>
            <p className="text-base text-gray-800">{d}</p>
          </div>
        ))}
      </div>
      <div className="rounded-xl p-4 mt-4" style={{ background: '#fff', border: '1px solid #CBD5E1' }}>
        <div className="text-sm font-extrabold uppercase tracking-wide text-gray-600 mb-2">{HANDICAP_GUIDE.example.title}</div>
        {HANDICAP_GUIDE.example.lines.map((l, i) => <p key={i} className="text-base text-gray-800 mb-1.5">{l}</p>)}
      </div>
    </div>
  )
}

export default function GGamesPlay() {
  const [view, setView] = useState('home') // home | setup | play | results
  const [gameId, setGameId] = useState(null)
  const [gameKey, setGameKey] = useState(null)
  const [games, setGames] = useState([])
  const [guideKey, setGuideKey] = useState(null)
  const [showHcp, setShowHcp] = useState(false)
  const [editGame, setEditGame] = useState(null)   // GGC: loaded record when editing a saved game
  const [editGameId, setEditGameId] = useState(null)
  const navigate = useNavigate()
  useEffect(() => watchGames(setGames), [])
  // Deep-link: /admin/gg/games?start=<key> opens that game's setup directly
  // (hub tiles link here so tapping a game launches its real play flow).
  useEffect(() => {
    const s = new URLSearchParams(window.location.search).get('start')
    if (s && getGameDef(s)) { setGameKey(s); setView('setup') }
  }, [])
  // Deep-link: /admin/gg/games?edit=<id> — Saved Games opens an existing game back
  // into the setup form, fully editable, saving to the SAME /ggGames/{id}.
  useEffect(() => {
    const e = new URLSearchParams(window.location.search).get('edit')
    if (!e) return
    loadMemberGame(e).then((g) => {
      if (g) { setEditGame(g); setEditGameId(e); setGameKey(g?.meta?.gameId || 'wolf'); setView('setup') }
    }).catch(() => {})
  }, [])

  const startSetup = (key) => { setGameKey(key); setView('setup') }
  // Import a game saved to a file: parse + validate, then load it into the setup
  // form (as a NEW game — editGameId stays null so Send writes a fresh /ggGames
  // record under the signed-in uid, not an overwrite). Re-hydration happens via
  // GGameSetup's gameToSetupState(initialGame).
  const onImport = async () => {
    try {
      const rec = await pickGameFile()
      if (!rec) return
      setEditGame(rec)
      setEditGameId(null)
      setGameKey((rec.meta && rec.meta.gameId) || 'wolf')
      setView('setup')
    } catch (e) {
      alert(e && e.message ? e.message : 'That file could not be imported.')
    }
  }
  const open = (g) => { setGameId(g.id); setView(g.meta?.status === 'final' ? 'results' : 'play') }
  const backToHub = () => navigate('/admin/gg-features')

  if (view === 'setup') return <GGameSetup gameKey={gameKey} initialGame={editGame} editGameId={editGameId} onStart={(id) => { setGameId(id); setView('play') }} onCancel={editGameId ? backToHub : () => setView('home')} />
  if (view === 'play') return <GScorecard gameId={gameId} onFinish={() => setView('results')} onExit={() => setView('home')} />
  if (view === 'results') return <GGameResults gameId={gameId} onExit={() => setView('home')} onBackToScorecard={() => setView('play')} />

  return (
    <div className="card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-bold text-xl">How To Play — Click a Game Tile</h3>
          <p className="text-sm text-gray-500 mb-4">Tap any game to read how it’s played and see an example setup.</p>
        </div>
        <button onClick={onImport} className="rounded-xl px-4 py-2 text-base font-extrabold shrink-0"
          title="Load a game you saved to a file, then Send it to the app"
          style={HUSTLER ? { background: '#121212', border: '1.5px solid #F2C400', color: '#F2C400' } : { background: '#EDF4EE', border: '1.5px solid #1B4D2E', color: GREEN }}>
          ⬆︎ Import game
        </button>
      </div>

      <div className="flex flex-wrap gap-3 mb-2">
        {PLAYABLE_GAMES.map((g) => <GameTile key={g.key} game={g} onGuide={setGuideKey} selected={guideKey === g.key} />)}
      </div>

      {guideKey && <GameGuide game={getPlayable(guideKey)} guide={GAME_GUIDES[guideKey]} />}

      <div className="mt-5">
        <button onClick={() => setShowHcp((v) => !v)} className="w-full text-left rounded-xl p-4 text-base font-extrabold" style={HUSTLER ? { background: '#121212', border: '1.5px solid #F2C400', color: '#F2C400' } : { background: '#EAF2FB', border: '1.5px solid #4C7BB5', color: '#123D66' }}>
          🎯 Handicaps — how they make any game competitive for any handicap {showHcp ? '▲' : '▼  (tap to read)'}
        </button>
        {showHcp && <div className="mt-2"><HandicapGuide /></div>}
      </div>
    </div>
  )
}
